import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useOrganization } from '@/hooks/useOrganization';

interface ExperimentStep {
  id: string;
  day: number;
  title: string;
  type: string;
  description: string;
  meta?: Record<string, unknown>;
}

interface ExperimentProduct {
  fdc_id: number;
  brand_owner: string;
  gtin_upc: number;
  ingredients: string;
  serving_size: number;
  serving_size_unit: string;
  household_serving_fulltext: string;
  branded_food_category: string;
  description: string;
  title: string;
  brand_name: string;
  subbrand_name: string;
  package_weight: string;
  image_url: string;
}

interface Experiment {
  id: string;
  name: string;
  startDate: string;
  endDate: string | null;
  status: 'active' | 'completed' | 'draft';
  steps: ExperimentStep[];
  participantsCount?: number | null;
  experimentProtocol?: ApiExperimentProtocol;
  image?: string;
  description?: string;
  descriptionMarkdown?: string;
  price?: number;
  shopUrl?: string;
  landingPageUrl?: string;
  brandProductUrl?: string;
  qrUrl?: string;
  introductionUrl?: string;
  twilioFlowSid?: string;
  products?: ExperimentProduct[];
  shortCode?: string;
}

// Types for API response
interface ApiExperimentAction {
  meta?: Record<string, unknown>;
  type?: string;
  title?: string;
  description?: string;
}

interface ApiExperimentDay {
  title?: string;
  actions?: ApiExperimentAction[];
  description?: string;
}

interface ApiExperimentProtocol {
  days?: ApiExperimentDay[];
  [key: string]: unknown;
}

interface ApiExperiment {
  id: string;
  name: string;
  startDate?: string;
  endDate?: string | null;
  status?: string;
  participants_count?: number | null;
  experiment_protocol?: ApiExperimentProtocol;
  products?: ExperimentProduct[];
  [key: string]: unknown;
}

function mapApiExperimentToExperiment(apiExp: ApiExperiment): Experiment {
  // Map status from API to local type
  let status: Experiment['status'];
  switch ((apiExp.status || '').toLowerCase()) {
    case 'active':
      status = 'active';
      break;
    case 'completed':
      status = 'completed';
      break;
    case 'draft':
      status = 'draft';
      break;
    default:
      status = 'draft';
  }

  // Map steps from experiment_protocol.days
  let steps: ExperimentStep[] = [];
  if (
    apiExp.experiment_protocol &&
    Array.isArray(apiExp.experiment_protocol.days)
  ) {
    steps = apiExp.experiment_protocol.days.flatMap((day: ApiExperimentDay, dayIdx: number) =>
      Array.isArray(day.actions)
        ? day.actions.map((action: ApiExperimentAction, actionIdx: number) => ({
            id: `${apiExp.id}-d${dayIdx + 1}-a${actionIdx + 1}`,
            day: dayIdx + 1,
            title: action.title || '',
            type: action.type || '',
            description: action.description || '',
            meta: action.meta || {},
          }))
        : []
    );
  }

  return {
    id: apiExp.id,
    name: apiExp.name,
    startDate: apiExp.startDate || '',
    endDate: apiExp.endDate || null,
    status,
    steps,
    participantsCount: apiExp.participants_count,
    experimentProtocol: apiExp.experiment_protocol,
    image: apiExp.image_url as string | undefined,
    description: apiExp.description as string | undefined,
    descriptionMarkdown: apiExp.description_markdown as string | undefined,
    price: apiExp.price_usd as number | undefined,
    shopUrl: apiExp.shop_url as string | undefined,
    landingPageUrl: apiExp.landing_page_url as string | undefined,
    brandProductUrl: apiExp.brand_product_url as string | undefined,
    qrUrl: apiExp.qr_url as string | undefined,
    introductionUrl: apiExp.introduction_url as string | undefined,
    twilioFlowSid: apiExp.twilio_flow_sid as string | undefined,
    products: apiExp.products || [],
    shortCode: apiExp.short_code as string | undefined,
  };
}

export const useExperiments = () => {
  const { organization } = useOrganization();

  return useQuery({
    queryKey: ['experiments', organization?.id],
    queryFn: async (): Promise<Experiment[]> => {
      if (!organization?.id) {
        return [];
      }
      // First get the experiment IDs for this organization
      const { data: orgExperiments, error: orgError } = await supabase
        .from('organization_experiments')
        .select('experiment_id, reference_name')
        .eq('organization_id', organization.id);
      if (orgError) {
        throw new Error(orgError.message || 'Failed to fetch organization experiments');
      }
      if (!orgExperiments || orgExperiments.length === 0) {
        return [];
      }
      // Get the actual experiment data from the Tastermonial API
      const { data: apiResponse, error } = await supabase.functions.invoke('tastermonial-api', {
        body: {
          method: 'GET',
          endpoint: '/api/v2/experiments',
        },
      });
      if (error) {
        throw new Error(error.message || 'Failed to fetch experiments');
      }
      if (apiResponse.status !== 200) {
        throw new Error(`API error: ${apiResponse.statusText || 'Failed to fetch experiments'}`);
      }
      const experimentsData = apiResponse.data;
      const experimentIds = (orgExperiments as { experiment_id: string }[]).map((oe) => oe.experiment_id);
      // Map and filter experiments
      const filteredExperiments = (Array.isArray(experimentsData) ? experimentsData : [])
        .filter((exp: ApiExperiment) => experimentIds.includes(exp.id))
        .map(mapApiExperimentToExperiment);
      return filteredExperiments;
    },
    enabled: !!organization?.id,
  });
};

export const useExperimentDetails = (experimentId: string) => {
  const { organization } = useOrganization();

  return useQuery({
    structuralSharing: false,
    queryKey: ['experiment', experimentId, organization?.id],
    queryFn: async (): Promise<Experiment> => {
      if (!organization?.id) {
        throw new Error('No organization selected');
      }
      // Verify this experiment belongs to the user's organization
      const { data: orgExperiment, error: orgError } = await supabase
        .from('organization_experiments')
        .select('experiment_id')
        .eq('organization_id', organization.id)
        .eq('experiment_id', experimentId)
        .single();
      if (orgError) {
        throw new Error('Experiment not found or access denied');
      }
      if (!orgExperiment) {
        throw new Error('Experiment not found for this organization');
      }
      // Fetch experiment details from the Tastermonial API
      const { data: apiResponse, error: experimentsError } = await supabase.functions.invoke('tastermonial-api', {
        body: {
          method: 'GET',
          endpoint: `/api/v2/experiments/${experimentId}`,
        },
      });
      if (experimentsError) {
        throw new Error(experimentsError.message || 'Failed to fetch experiment details');
      }
      if (apiResponse.status !== 200) {
        throw new Error(`API error: ${apiResponse.statusText || 'Failed to fetch experiment details'}`);
      }
      // The response is now a single experiment object
      const apiExp = apiResponse.data as ApiExperiment;
      console.log('Returning new experiment details:', apiExp);
      return mapApiExperimentToExperiment(apiExp);
    },
    enabled: !!experimentId && !!organization?.id,
  });
};
